import { useEffect } from 'react';
import { POSE_CONNECTIONS } from '@/types/mediapipe';
import type { LandmarkDeviationWithCoords } from '@/types/visualization';
import { hexToRgba } from '@/utils/colors';
import { cn } from '@/utils/cn';
import { setupCanvas } from '@/utils/canvasHelpers';
import {
  getThresholdScoreColor,
  SCORE_THRESHOLDS,
  SCORE_COLORS,
  RENDERING,
  CANVAS_DRAWING,
  isLandmarkVisible,
} from '@/utils/visualizationConstants';

interface DifferentialSkeletonOverlayProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  landmarks: Array<{ x: number; y: number; visibility?: number }>; // 33 landmarks, normalized 0-1
  landmarkDeviations?: LandmarkDeviationWithCoords[]; // Per-landmark scores with coords
  /** Width of the actual video render area */
  width: number;
  /** Height of the actual video render area */
  height: number;
  /** X offset from canvas left edge to the video render area (pillarbox offset) */
  offsetX?: number;
  /** Y offset from canvas top edge to the video render area (letterbox offset) */
  offsetY?: number;
  /** Full container width — canvas is sized to this */
  containerWidth?: number;
  /** Full container height — canvas is sized to this */
  containerHeight?: number;
  intensity: number; // 0-100, controls overlay intensity
  currentFrame: number;
  fallbackScore?: number | null; // Frame-level score to use when no per-landmark data
  className?: string;
}

function DifferentialSkeletonOverlay({
  canvasRef,
  landmarks,
  landmarkDeviations = [],
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  containerWidth,
  containerHeight,
  intensity,
  currentFrame,
  fallbackScore,
  className,
}: DifferentialSkeletonOverlayProps) {
  const canvasW = containerWidth ?? width;
  const canvasH = containerHeight ?? height;

  useEffect(() => {
    const ctx = setupCanvas(canvasRef, canvasW, canvasH);
    if (!ctx) return;

    try {
      // Skip rendering if intensity is too low (nearly invisible)
      if (intensity < RENDERING.MIN_INTENSITY) return;

      if (!landmarks || landmarks.length === 0) return;

    // Build a map of landmark_id -> deviation_score for the current frame
    const deviationMap = new Map<number, number>();
    (landmarkDeviations ?? [])
      .filter((d) => d.frame_index === currentFrame)
      .forEach((d) => {
        deviationMap.set(d.landmark_id, d.deviation_score);
      });

    // Helper to get score for a landmark
    // Returns fallbackScore if no deviation data exists, or null if no fallback
    const getScore = (landmarkId: number): number | null => {
      const score = deviationMap.get(landmarkId);
      if (score !== undefined) return score;
      // Use fallback frame score if available
      if (fallbackScore !== null && fallbackScore !== undefined) {
        return fallbackScore;
      }
      return null;
    };

    // Helper to get color based on score (0-100 range)
    // Returns neutral grey if score is null (no deviation data and no fallback)
    const getColorForScore = (score: number | null): string => {
      if (score === null) {
        return SCORE_COLORS.NEUTRAL;
      }
      return getThresholdScoreColor(score);
    };

    const baseOpacity = intensity / 100;

    // Draw connections (bones) colored by average score of endpoints
    POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
      const start = landmarks[startIdx];
      const end = landmarks[endIdx];

      if (!start || !end) return;

      // Skip if visibility is too low
      if (!isLandmarkVisible(start) || !isLandmarkVisible(end)) return;

      // Get scores for both endpoints
      const startScore = getScore(startIdx);
      const endScore = getScore(endIdx);

      // Calculate bone score: average if both have data, use single if only one has data, null if neither
      let boneScore: number | null = null;
      if (startScore !== null && endScore !== null) {
        boneScore = (startScore + endScore) / 2;
      } else if (startScore !== null) {
        boneScore = startScore;
      } else if (endScore !== null) {
        boneScore = endScore;
      }

      // Get color based on bone score
      const boneColor = getColorForScore(boneScore);

      // Determine if bone is "good" or "bad" for glow effect (only if we have a score)
      const isGood = boneScore !== null && boneScore >= SCORE_THRESHOLDS.GOOD;
      const isBad = boneScore !== null && boneScore < SCORE_THRESHOLDS.MEDIUM;

      // Apply glow effect based on score
      if (isGood) {
        ctx.shadowColor = boneColor;
        ctx.shadowBlur = CANVAS_DRAWING.GLOW_BLUR_SOFT;
      } else if (isBad) {
        ctx.shadowColor = boneColor;
        ctx.shadowBlur = CANVAS_DRAWING.GLOW_BLUR_MEDIUM;
      } else {
        ctx.shadowBlur = 0;
      }

      // Draw the connection
      ctx.beginPath();
      ctx.moveTo(start.x * width + offsetX, start.y * height + offsetY);
      ctx.lineTo(end.x * width + offsetX, end.y * height + offsetY);
      ctx.strokeStyle = hexToRgba(boneColor, baseOpacity);
      ctx.lineWidth = CANVAS_DRAWING.LINE_WIDTH;
      ctx.stroke();
    });

    // Reset shadow
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';

    // Draw landmarks colored by their deviation score
    landmarks.forEach((landmark, index) => {
      if (!isLandmarkVisible(landmark)) return;

      const score = getScore(index);
      const landmarkColor = getColorForScore(score);

      // Add subtle glow to significant landmarks (only if we have deviation data)
      if (score !== null && score < SCORE_THRESHOLDS.MEDIUM) {
        ctx.shadowColor = landmarkColor;
        ctx.shadowBlur = CANVAS_DRAWING.GLOW_BLUR_SOFT;
      }

      ctx.beginPath();
      ctx.arc(landmark.x * width + offsetX, landmark.y * height + offsetY, CANVAS_DRAWING.LANDMARK_RADIUS, 0, 2 * Math.PI);
      ctx.fillStyle = hexToRgba(landmarkColor, baseOpacity);
      ctx.fill();

      // Contrast ring — the SAME treatment as the coach panel's PoseOverlay
      // dots (canvasUtils.drawPoint: dark 1px outline). The previous border
      // used the landmark's own color at 0.8 opacity, so joints melted into
      // the same-colored bones and the video — the owner couldn't "find the
      // pointes" on the athlete side (2026-08-21 request, self-eval
      // 9440d89c screenshot). Fill keeps the deviation-score color; only the
      // ring is fixed dark for contrast.
      ctx.strokeStyle = hexToRgba('#000000', Math.max(baseOpacity, 0.8));
      ctx.lineWidth = CANVAS_DRAWING.BORDER_LINE_WIDTH;
      ctx.stroke();

      // Reset shadow
      ctx.shadowBlur = 0;
    });
    } catch (error) {
      console.error('[DifferentialSkeletonOverlay] Canvas drawing error:', error);
    }
  }, [canvasRef, landmarks, landmarkDeviations, width, height, offsetX, offsetY, canvasW, canvasH, intensity, currentFrame, fallbackScore]);

  return (
    <canvas
      ref={canvasRef}
      width={canvasW}
      height={canvasH}
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{ width: '100%', height: '100%' }}
      aria-label="Differential skeleton overlay"
    />
  );
}

export default DifferentialSkeletonOverlay;
